define('bindingHandlers/binaryHeatMap',[
    'jquery',
    'moment',
    'lib/lodash4',
    'lib/knockout',
    'lib/d3',

    'util/domUtils',
    'util/d3Utils',

    'bindingHandlers/dataViz/components/svgContainer',

    'extensions/i18n/knockout.i18n'
], function (
    $,
    moment,
    _,
    ko,
    d3,
    domUtils,
    d3Utils,

    renderSvgContainer
) {
    'use strict';

    //
    // Chart Dimensions
    //
    var BAR_MARGIN = {
        right: 5,
        bottom: 10
    };

    var BAR_HEIGHT = 20;
    var LABEL_WIDTH = 130;
    var LABEL_MARGIN = 10;
    var LABEL_TICK_LINE_LENGTH = 6;

    var X_AXIS_HEIGHT = 20;

    var CANVAS_CLASS = "chart-canvas";
    var POSITIVE_VALUE_CLASS = "positive-fill";

    //
    // Utility Functions
    //

    var getValueFromDataObject = function (dataObj, key) {
        if (key) {
            return ko.unwrap(dataObj[key]);
        }

        return dataObj;
    };

    var getCategoryTextFromDataObject = function (dataObj, key) {
        if (key) {
            return ko.unwrap(dataObj[key]);
        }

        return null;
    };

    var generateXAxisScale = function (xAxisSettings, rangeArr) {
        var domain = ko.unwrap(xAxisSettings.domain);
        return d3.time.scale()
                    .domain(domain)
                    .range(rangeArr)
                    .nice(d3.time.minute);
    };

    var renderYAxisLine = function (svg, height) {
        var yAxisLine = svg.select(".y-axis-line");

        if (yAxisLine.empty()) {
            // create the line if one does not exist
            svg.append("line")
                .attr("class", "y-axis-line")
                .attr("x1", 0)
                .attr("y1", 0)
                .attr("x2", 0)
                .attr("y2", height)
                .attr("transform", "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + ",0)");
        } else {
            // Update the height of the line to the provided height
            yAxisLine.attr("y2", height);
        }
    };

    var renderLabels = function (svg, dataCollection, yScale, bindingData) {
        var getLabel = function (dataObj) {
            return getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);
        };

        var labelClasses = {
            "chart-label": true
        };

        // Join new data with existing data
        var labels = svg.selectAll(".chart-label")
                            .data(dataCollection);

        //
        // Create new labels
        //
        var labelsEnter = labels.enter();

        if (bindingData.categoryHrefKey){
            labelsEnter = labelsEnter
                .append("a")
                .attr("xlink:href", function (dataObj) {
                    return ko.unwrap(dataObj[bindingData.categoryHrefKey]);
                })
                .attr("target", "_parent");
        }

        labelsEnter
                .append("text")
                .classed(labelClasses) // conditionally set the label's classes
                .attr("x", 0)
                .attr("y", function (dataObj) {
                    var label = getLabel(dataObj);
                    return yScale(label) + yScale.rangeBand()/2;
                })
                .attr("dy", ".35em")
                .attr("text-anchor", "start")
                .attr("data-bind", function (dataObj) {
                    return "tooltip: \"" + getLabel(dataObj) + "\"";
                })
                .text(d3Utils.truncateText(
                        function (dataObj) {
                            return getLabel(dataObj);
                        },
                        function () {
                            return LABEL_WIDTH;
                        }
                    )
                );

        //
        // Update Existing Labels
        //
        labels.transition()
                .duration(250)
                    .style("fill-opacity", 1);

        //
        // Remove Old Labels
        //
        labels.exit()
                .transition()
                    .duration(250)
                    .style("fill-opacity", 0)
                .remove();
    };

    var renderRows = function (svg, dataCollection, xScale, yScale, bindingData) {

        var generateYValue = function (dataObj) {
            var value = getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);
            return yScale(value) + yScale.rangeBand()/2;
        };

        // ===============================================================================
        // Render Parent Rows
        // ===============================================================================

        // Create the rows of data
        var rows = svg.selectAll(".row")
                        .data(dataCollection);

        // Create the tick mark on the y-axis for each row
        rows.enter()
            .append("line")
                .attr("x1", LABEL_WIDTH + LABEL_MARGIN)
                .attr("x2", LABEL_WIDTH + LABEL_MARGIN - LABEL_TICK_LINE_LENGTH)
                .attr("y1", generateYValue)
                .attr("y2", generateYValue);

        // Create the group node that will contain the content for each row
        rows.enter()
            .append("g")
                .attr("class", "row")
                .attr("transform", function (dataObj) {
                    var value = getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);

                    return "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + "," + yScale(value) + ")";
                });

        // ===============================================================================
        // Render Child Blocks
        // ===============================================================================

        // Generate a block for each tick in the xScale

        var blocks = rows.selectAll(".block")
                        .data(function (rowData) {
                            return getValueFromDataObject(rowData, bindingData.valueKey);
                        });

        var blocksEnter = blocks.enter();

        //
        // Create New Blocks
        //

        // Create each block
        var BLOCK_WIDTH = (Math.abs(xScale.range()[0] - xScale.range()[1])/1.5)/48;
        var BLOCK_PADDING = {
            left: 2,
            right: 3
        };

        blocksEnter.append("rect")
            .attr("x", function(d, i) {
                return xScale(i) + BLOCK_PADDING.left;
            })
            .attr("y", 0)
            .attr("height", yScale.rangeBand())
            .attr("width", BLOCK_WIDTH);

        //
        // Update Existing Blocks
        //
        blocks.attr("class", function(d) {
                var className = "block";
                var datumBoolValue = !!d;

                if (_.isNull(d)) {
                    className += " empty-fill";
                } else if (datumBoolValue) {
                    className += " " + POSITIVE_VALUE_CLASS;
                }

                return className;
            });

        //
        // Remove Old Bars
        //
        blocks.exit()
                .transition()
                    .duration(250)
                    .style("fill-opacity", 0)
                .remove();
    };

    var renderXAxis = function (element, xScale) {
        var isAxisCreated = function (element) {
            return $("svg.x.axis", element).length > 0;
        };

        var xAxis = d3.svg.axis()
            .scale(xScale)
            .ticks(d3.time.hours, 4)
            .orient("bottom")
            .tickFormat(function (datum, i) {
                if ( i % 2 === 1) {
                    // Some locales need to use en-US format (see UI-2365, UI-2495)
                    var locale = ko.i18n.getLocale();
                    var useEnglishFormat = ["th", "zh-CN", "zh-TW", "ja", "ko"].indexOf(locale) > -1;
                    return ko.i18n.formatDate(datum, "LT", useEnglishFormat ? 'en-US' : locale);
                }

                return "";
            });

        if (!isAxisCreated(element)) {
            // Create the nodes that the axis will live in
            var xAxisNode = d3.select(element)
                                .append("svg")
                                     .attr("class", "x axis")
                                     .attr("height", X_AXIS_HEIGHT);

            xAxisNode.append("g")
                  .attr("transform", "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + ",0)")
                  .call(xAxis);

            var $xAxisTitleNode = $(".x-axis.axis-title", element);
            $(xAxisNode[0]).insertBefore($xAxisTitleNode);

        } else {
            // Update the axis
            d3.select(element).select(".x.axis g")
                .transition()
                .call(xAxis);
        }
    };

    /**
     * Create a binary heat map that will chart out colored blocks for each tick on the
     * x-axis. The color of these blocks are determined by the binary value for an entry -
     * a truthy value will result in a "positive" color while a falsy value will result in
     * an eye-catching "negative color".
     *
     * **TODO**: As of 5/22/14, the arguments "valueKey" and "categoryTextKey" are required
     * when they should be completely optional. As such, to get this binding handler to work,
     * you need to pass in a complex object with an attribtue that contains a label string
     * and an attribute that contains a value string. THIS NEEDS TO CHANGE - this binding
     * should support the ability to pass in an array of basic values to create bars. In
     * this case, the labels should be the index number of value's spot in the collection.
     *
     * **TODO** (6/20/14): As more axis types need to become supported, I recommend adding
     * a "type" attribute to the xAxis argument object to determine which type of scale
     * should be used by D3 to help generate the axis.
     *
     * # Required Arguments
     *
     * @param {Array[]|Object[]|} [data] A multidimensional array where each entry
     *  represents a row in the chart and the entry's value array contains the data that
     *  will be used to populate the row's data.
     *
     *  An array of objects is acceptable to pass in. If done so, then the optional
     *  arguments of 'valueKey' and 'categoryTextKey' can be used to declare which
     *  attributes contain a row's label.
     *
     *  Since this is a binary heat map, each entry in array of values will be treated as
     *  booleans to determine the value displayed by the chart. The one exception to this
     *  are entries with a value of "null". If a null value is encountered, then the
     *  renderer will render an "empty" value for that entry - nothing will be displayed
     *  for that entry.
     *
     *  The location of each value entry will also correspond to its location on the
     *  x-axis. The max width of the x-axis is determined by the largest collection of
     *  values in the data series. This means that two rows have the following value
     *  arrays:
     *
     *  [
     *    [0, 1, 1, 0, 1] // Row 1
     *    [0, 1, 0, 1, 1, 0, 0, 1, 1, 1] // Row 2
     *  ]
     *
     *  Then the locations of the values for Row 1 will be aligned with the first 5
     *  entries of Row 2.
     *
     *
     * @param {Object[]} options.data - See above description for "data"
     *
     * # Optional Arguments
     *
     * @param {Object} [options.xAxis=undefined] An object containing settings for the
     *  x-axis of the chart.
     * @param {Date[]} [options.xAxis.domain=undefined] An array that contains two items
     *  that represent the minimum and maximum values of the domain that should be used to
     *  display in the x-axis of the chart. If not provided, an x-axis will not be rendered.
     * @param {String} [options.xAxis.title=undefined] - The message key that to use for
     *  the title of the chart's X axis
     *
     * @param {Object} [options.yAxis=undefined] An object containing settings for the
     *  y-axis of the chart
     * @param {String} [options.yAxis.title=undefined] - The message key that to use for
     *  the title of the chart's Y axis.
     *
     * @param {Object} [options.event=undefined] An object containing settings for
     *  user events that occur in the chart.
     * @param {Function} [options.event.scroll] A callback function that will be called
     *  whenever the user scrolls near canvas to the bottom of its container. NOTE: UX
     *  has final say as to whether using scroll events to incoporate an infinite scroll
     *  interaction should be done.
     *
     * @param {String} [options.valueKey=undefined] A string that represents the attribute
     *  key on each object in the data array that should be used as the value to display.
     * @param {String} [options.categoryTextKey=undefined] - A string that represents the
     *  attribute key on each object in the data array that should be used as the text to
     *  display for each category in the chart.
     *
     *
     */
    ko.bindingHandlers.binaryHeatMap = {
        init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
            var bindingData = ko.unwrap(valueAccessor());

            var $element = $(element);
            var $yAxisTitleNode;

            $element.addClass("chart chart-binary-heatmap");

            //
            // Create node that will contain the y-axis title and key content
            //

            var $headerRowElement = $element;

            if (bindingData.yAxis || bindingData.key) {
                $headerRowElement = $("<div/>", {
                    "class": "chart-header"
                }).prependTo($element);
            }

            if (bindingData.yAxis && bindingData.yAxis.title) {
                $yAxisTitleNode = $("<div/>", {
                    "class": "axis-title y-axis",
                    "text": ko.i18n.renderString(bindingData.yAxis.title)
                }).appendTo($headerRowElement);
            }

            if (bindingData.key) {
                if (!bindingData.key.positiveTitleMessageKey || !bindingData.key.negativeTitleMessageKey) {
                    throw new Error("The binaryHeatMap binding's key argument object requires one to declare a message key for both 'positiveTitleMessageKey' and 'negativeTitleMessageKey'.");
                }

                var $keyNode = $("<ul/>", {
                    "class": "key list-unstyled list-inline",
                }).appendTo($headerRowElement);

                $("<li/>", {"class": "key-item"})
                    .appendTo($keyNode)
                    .append([
                        $("<span/>", {"class": "key-symbol block " + POSITIVE_VALUE_CLASS}),
                        $("<span/>", {
                            "class": "key-title",
                            "text": ko.i18n.renderString(bindingData.key.positiveTitleMessageKey)
                        })
                    ]);

                $("<li/>", {"class": "key-item"})
                    .appendTo($keyNode)
                    .append([
                        $("<span/>", {"class": "key-symbol block"}),
                        $("<span/>", {
                            "class": "key-title",
                            "text": ko.i18n.renderString(bindingData.key.negativeTitleMessageKey)
                        })
                    ]);
            }

            // Create node that will contain the x-axis title

            if (bindingData.xAxis && bindingData.xAxis.title) {
                $("<div/>", {
                    "class": "axis-title x-axis",
                    "text": ko.i18n.renderString(bindingData.xAxis.title)
                }).css({
                    "margin-left": (LABEL_WIDTH + LABEL_MARGIN) + "px"
                }).appendTo($element);
            }

            //
            // Create and insert the scrollable chart canvas
            //

            var canvasNode = $("<div/>", {
                "class": CANVAS_CLASS
            });

            if ($yAxisTitleNode) {
                canvasNode.insertAfter($headerRowElement);
            } else {
                $element.prepend(canvasNode);
            }

            // Hook up the canvas container to the scrolled event
            if (bindingData.event && bindingData.event.scroll) {
                ko.bindingHandlers.scrolled.init(
                    canvasNode[0],
                    function () {
                        return bindingData.event.scroll;
                    },
                    allBindings,
                    viewModel,
                    bindingContext
                );
            }
        },

        update: function (element, valueAccessor) {
            var $element = $(element);
            var chartContentContainer = $("." + CANVAS_CLASS, element)[0];

            var bindingData = ko.unwrap(valueAccessor());
            var seriesData = _.isPlainObject(bindingData) ? ko.unwrap(bindingData.data) : bindingData;
            var xAxis = _.isPlainObject(bindingData) ? ko.unwrap(bindingData.xAxis) : undefined;

            if (seriesData.length === 0) {
                return;
            }

            var labels = _.map(seriesData, function (obj) {
                return getCategoryTextFromDataObject(obj, bindingData.categoryTextKey);
            });

            //
            // Generate Chart Canvas
            //
            var CONTENT_HEIGHT = (BAR_HEIGHT + BAR_MARGIN.bottom) * seriesData.length;
            var LINE_HEIGHT = Math.max(CONTENT_HEIGHT, $(chartContentContainer).height());

            var barChartSvg = renderSvgContainer(chartContentContainer, CONTENT_HEIGHT);

            //
            // Configure Scales
            //

            var MAX_BAR_WIDTH =
                    $element.width() -
                    BAR_MARGIN.right -
                    domUtils.getScrollbarWidth() -
                    LABEL_WIDTH -
                    LABEL_MARGIN -
                    LABEL_TICK_LINE_LENGTH;

            var x = d3.scale.linear()
                .domain([
                    0,
                    d3.max(seriesData, function (entry) {
                        return getValueFromDataObject(entry, bindingData.valueKey).length;
                    })
                ])
                .range([0, MAX_BAR_WIDTH]);

            var y = d3.scale.ordinal()
                        .domain(labels)
                        .rangeRoundBands([0, CONTENT_HEIGHT], 0.4, 0);

            //
            // Render Chart Content
            //
            renderYAxisLine(barChartSvg, LINE_HEIGHT);
            renderRows(barChartSvg, seriesData, x, y, bindingData);
            renderLabels(barChartSvg, seriesData, y, bindingData);

            if (xAxis) {
                var xAxisScale = generateXAxisScale(xAxis, [0, MAX_BAR_WIDTH]);

                renderXAxis(element, xAxisScale);
            }
        }
    };

});

